import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Promise-based confirm dialog — a styled, on-brand replacement for the native
 * window.confirm (which Chrome can suppress, and which looks nothing like the
 * app). Mirrors the notify/react-hot-toast singleton pattern: call confirm()
 * from anywhere and await the boolean; a single <ConfirmHost/> mounted near the
 * Toaster renders the UI.
 *
 *   if (await confirm({ title: 'Delete course?', tone: 'danger' })) { ... }
 */

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'default';
  /**
   * Offer a "don't show this again" tick. Present only where the dialog is a
   * HEADS-UP rather than a safety gate: a warning worth showing once is worth
   * being able to silence, and one that must be read every time should not
   * offer the option at all.
   *
   * The confirm resolves to `{ ok, dontShowAgain }` when this is set, so the
   * caller decides what "again" means and where to remember it.
   */
  dismissible?: { label?: string };
}

type Request = ConfirmOptions & { id: number; resolve: (r: { ok: boolean; dontShowAgain: boolean }) => void };

let queue: Request[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

/** Ask the user to confirm. Resolves true (confirmed) or false (cancelled). */
export interface ConfirmResult { ok: boolean; dontShowAgain: boolean }

/** Ask, and also hear whether they ticked "don't show this again". */
export function confirmWithOption(options: ConfirmOptions): Promise<ConfirmResult> {
  return ask(options);
}

/** Ask the user to confirm. Resolves true (confirmed) or false (cancelled). */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return ask(options).then(r => r.ok);
}

function ask(options: ConfirmOptions): Promise<ConfirmResult> {
  return new Promise(resolve => {
    // NO HOST MOUNTED — resolve rather than queue forever.
    //
    // This module is a singleton queue with a listener set. Called from a tree
    // that renders before (or without) <ConfirmHost/>, the request used to sit
    // in the queue with nobody to draw it, and the promise never settled: the
    // caller awaited an answer that could not arrive. That is the same
    // dead-button failure this whole module replaced, wearing a spinner instead
    // of returning false.
    //
    // Falls back to the native dialog because it at least ASKS, and returns
    // false if even that is unavailable — never silently proceeding with a
    // destructive action nobody confirmed.
    if (listeners.size === 0) {
      console.error('[confirm] No <ConfirmHost/> is mounted — falling back to the native dialog.', options.title);
      const text = [options.title, options.message].filter(Boolean).join('\n\n');
      resolve({
        ok: typeof window !== 'undefined' && typeof window.confirm === 'function' ? window.confirm(text) : false,
        dontShowAgain: false,
      });
      return;
    }
    queue = [...queue, { ...options, id: nextId++, resolve }];
    emit();
  });
}

/** Mount once, near the app's <Toaster/>. Renders the active confirm request. */
export const ConfirmHost: React.FC = () => {
  const [, force] = useState(0);
  /** Reset per request in close(), so a tick on one dialog never carries to the next. */
  const [dontShowAgain, setDontShowAgain] = useState(false);
  useEffect(() => {
    const l = () => force(n => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  const current = queue[0];

  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => {
      // Escape is a cancel, so it never records a "don't show again".
      if (e.key === 'Escape') { current.resolve({ ok: false, dontShowAgain: false }); queue = queue.slice(1); emit(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [current]);

  if (!current) return null;

  const close = (ok: boolean) => {
    current.resolve({ ok, dontShowAgain });
    setDontShowAgain(false);
    queue = queue.slice(1);
    emit();
  };

  const danger = current.tone === 'danger';
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={() => close(false)}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${danger ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600'}`}>
            <AlertTriangle size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900">{current.title}</h3>
            {current.message ? (
              <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">{current.message}</p>
            ) : null}
          </div>
        </div>
        {current.dismissible && (
          <label className="mt-4 flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={e => setDontShowAgain(e.target.checked)}
              className="rounded border-gray-300"
            />
            {current.dismissible.label ?? "Don't show this again"}
          </label>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={() => close(false)}
            className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            {current.cancelLabel ?? 'Cancel'}
          </button>
          <button
            onClick={() => close(true)}
            autoFocus
            className={`px-4 py-2 rounded-xl text-sm font-semibold text-white ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
          >
            {current.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};
